import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, Phone, Github, Linkedin, Send, MessageCircle, ArrowUpRight, Copy, Check } from 'lucide-react';
import { profileData } from '../data/mock';

const PROFILE_IMAGE = "https://customer-assets.emergentagent.com/job_motion-profile-3/artifacts/mxrz3tmc_Screenshot%202026-02-01%20at%202.22.10%E2%80%AFPM.png";

const ContactCard = ({ icon: Icon, label, value, href, color, delay }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!href.startsWith('http')) {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true }}
      className="group"
    >
      <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 hover:border-transparent transition-all duration-300 relative overflow-hidden backdrop-blur-sm">
        {/* Hover gradient */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: `radial-gradient(circle at 30% 30%, ${color}12, transparent 70%)`
          }}
        />

        <div className="relative z-10">
          <div className="flex items-start justify-between mb-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${color}20, ${color}10)` }}
            >
              <Icon size={24} style={{ color }} />
            </div>
            
            {href.startsWith('http') ? (
              <motion.a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowUpRight size={18} className="text-white/60" />
              </motion.a>
            ) : (
              <motion.button
                onClick={handleCopy}
                className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {copied ? (
                  <Check size={18} className="text-[#f5d89a]" />
                ) : (
                  <Copy size={18} className="text-white/60" />
                )}
              </motion.button>
            )}
          </div>

          <h3 className="text-white/40 text-sm mb-1">{label}</h3>
          <p className="text-white font-medium truncate">{value}</p>
        </div>
      </div>
    </motion.div>
  );
};

const ContactSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: profileData.contact.email,
      href: `mailto:${profileData.contact.email}`,
      color: '#ffb79c'
    },
    {
      icon: Phone,
      label: 'Phone',
      value: profileData.contact.phone,
      href: `tel:${profileData.contact.phone}`,
      color: '#f5d89a'
    },
    {
      icon: Github,
      label: 'GitHub',
      value: 'pavanbobba09',
      href: profileData.contact.github,
      color: '#f4b8c5'
    },
    {
      icon: Linkedin,
      label: 'LinkedIn',
      value: 'Connect with me',
      href: profileData.contact.linkedIn,
      color: '#e8c4c4'
    }
  ];

  return (
    <section id="contact" ref={sectionRef} className="py-32 bg-[#0a0a0c] relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#ffb79c]/8 rounded-full blur-3xl" />
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#f5d89a]/8 rounded-full blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#ffb79c]/20 to-[#f5d89a]/10 border border-[#ffb79c]/20 flex items-center justify-center">
              <MessageCircle className="text-[#ffb79c]" size={24} />
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Let's Connect
          </h2>
          <p className="text-white/50 max-w-2xl mx-auto">
            Have a project in mind or just want to chat? I'd love to hear from you!
          </p>
        </motion.div>

        {/* Conversational CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-gradient-to-br from-[#ffb79c]/8 via-[#f5d89a]/5 to-[#a5d8ff]/5 border border-[#ffb79c]/15 rounded-3xl p-8 md:p-12 mb-12 text-center relative overflow-hidden backdrop-blur-sm"
        >
          {/* Animated soft glows */}
          <motion.div 
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="absolute top-4 right-4 w-32 h-32 rounded-full bg-[#ffb79c]/10 blur-2xl" 
          />
          <motion.div 
            animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 5, repeat: Infinity, delay: 1 }}
            className="absolute bottom-4 left-4 w-40 h-40 rounded-full bg-[#f5d89a]/10 blur-2xl" 
          />
          
          <div className="relative z-10">
            <motion.div
              initial={{ scale: 0 }}
              animate={isInView ? { scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="w-28 h-28 rounded-full overflow-hidden border-2 border-[#ffb79c]/30 mx-auto mb-6 shadow-xl shadow-[#ffb79c]/20"
            >
              <img src={PROFILE_IMAGE} alt={profileData.name} className="w-full h-full object-cover object-center scale-125" />
            </motion.div>

            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Thanks for scrolling this far!
            </h3>
            <p className="text-white/60 max-w-lg mx-auto mb-8 leading-relaxed">
              Whether you're looking to build something amazing, discuss AI/ML possibilities, 
              or just want to say hi — I'm always excited to connect with fellow tech enthusiasts.
            </p>

            <motion.a
              href={`mailto:${profileData.contact.email}`}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#ffb79c] to-[#ffd4c4] text-[#0d0d0f] font-semibold rounded-full hover:shadow-lg hover:shadow-[#ffb79c]/25 transition-all"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Send size={20} />
              Send me a message
            </motion.a>
          </div>
        </motion.div>

        {/* Contact Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {contactInfo.map((info, index) => (
            <ContactCard key={info.label} {...info} delay={0.1 * index} />
          ))}
        </div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16 pt-8 border-t border-white/10"
        >
          <p className="text-white/40 text-sm">
            Designed & Built by <span className="text-[#ffb79c]">{profileData.name}</span>
          </p>
          <p className="text-white/25 text-xs mt-2">
            © {new Date().getFullYear()} • Made with passion and lots of coffee
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
